import { useState, useEffect } from "react"
import Loader from "../../UI/Loader/Loader"
import { useContentfulContext } from "../../../contexts/ContentfulContext"
import { useUserContext } from "../../../contexts/UserContext"
import PrimaryModal from "../../UI/Modal/PrimaryModal"
import { SecondaryButton } from "../../UI";
import { getRecommendationById, saveEntityData } from "../../../utils/APIs"
import { toast } from "react-toastify"

const EditManageRecommendation = ({ id, handleClose, refetchData }) => {
    const { content } = useContentfulContext()
    const { userData } = useUserContext()
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [entityData, setEntityData] = useState({});

    useEffect(() => {
        getRecommendationData()
    }, [id])

    const getRecommendationData = async () => {
        try {
            setLoading(true);
            const result = await getRecommendationById({
                token: userData?.token,
                organizationId: userData?.selectedOrganization?.org_token,
                tenant: userData?.defaultAdobeIO?.tenant_name,
                id
            });
            setError("")
            const { attributes = {}, ...rest } = result.data || {};
            setEntityData({ ...rest, ...attributes });
        } catch (error) {
            const errMsg = error?.response?.data?.message || error?.message
            toast.error(errMsg || content?.COMMON_MESSAGE.something_wrong_msg)
            handleClose()
        } finally {
            setLoading(false);
        }
    }

    const renderFieldValue = (key, value) => {
        switch (key) {
            case "categories":
                return value.join(",");
            default:
                return value;
        }
    }

    const updateFieldValue = (key, value) => {
        switch (key) {
            case "categories":
                return value.split(",").map(item => item.trim());
            default:
                return value;
        }
    }

    const handleSubmit = async () => {
        try {
            setError("")
            setLoading(true)

            if (!id) {
                toast.error(content?.EDIT_RECOMMENDATION_ENTITY?.error_message || "Please select a valid recommendation to edit")
                return
            }

            const entity = {
                name: entityData["name"] || "",
                id: entityData["id"] || "",
                environment: entityData.environment,
                categories: entityData?.categories,
                attributes: {},
            }

            Object.keys(entityData).forEach((key) => {
                if (
                    key !== "categories" &&
                    key !== "category" &&
                    key !== "name" &&
                    key !== "id" &&
                    key !== "createdAt" &&
                    key !== "lastUpdatedAt" &&
                    key !== "environment"
                ) {
                    entity.attributes[key] = entityData[key] || ""
                }
            })

            const result = await saveEntityData(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                userData?.defaultAdobeIO?.tenant_name,
                {
                    entities: [entity],
                },
            )

            toast.success(content?.EDIT_RECOMMENDATION_ENTITY?.success_message || "Entity saved successfully")
            handleClose()
            refetchData && refetchData(false, true)
        } catch (error) {
            const errMsg = error?.response?.data?.message || error?.message || content?.EDIT_RECOMMENDATION_ENTITY?.errors?.save_failed
            toast.error(errMsg || content?.COMMON_MESSAGE.something_wrong_msg)
        } finally {
            setLoading(false)
        }
    }

    const excludeKeys = ["createdAt", "lastUpdatedAt", "id"];
    const disabledKeys = ["environment"];

    return (
        <>
            <PrimaryModal title={entityData?.id} hideModal={handleClose} widthClass="max-w-6xl">
                {loading && <Loader />}
                <div className="w-full p-6 space-y-4">

                    <div className="space-y-4">

                        <div className="max-w-7xl mx-auto">

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div className="h-[500px] w-[500px] ">
                                    {loading ? (
                                        <div className="animate-pulse h-full w-full bg-gray-200" />
                                    ) : (
                                        <img
                                            src={entityData?.thumbnailUrl || ""}
                                            alt={"Image URL broken or not available"}
                                            width={500}
                                            height={500}
                                            className="h-full w-full object-contain"
                                        />
                                    )}
                                </div>

                                <div className="space-y-6 max-h-[70vh] overflow-auto">
                                    <div className="grid gap-4 pt-1">
                                        {Object.entries(entityData || {})
                                            .filter(([key]) => !excludeKeys.includes(key))
                                            .map(([key, value]) => (
                                                <div className="relative">
                                                    <input
                                                        id={key}
                                                        name={key}
                                                        type="text"
                                                        placeholder=" "
                                                        disabled={disabledKeys.includes(key)}
                                                        value={renderFieldValue(key, value)}
                                                        onChange={(e) => {
                                                            if (disabledKeys.includes(key)) return;
                                                            setEntityData(prev => ({
                                                                ...prev,
                                                                [key]: updateFieldValue(key, e.target.value)
                                                            }));
                                                        }}
                                                        className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary peer"
                                                    />
                                                    <label
                                                        htmlFor={key}
                                                        className="absolute text-sm text-black duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-primary peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1"
                                                    >
                                                        {key.charAt(0).toUpperCase() + key.slice(1)}
                                                    </label>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end flex-end mt-4">
                                <SecondaryButton handleClick={handleSubmit}>
                                    {content?.EDIT_RECOMMENDATION_ENTITY?.submit_button || "Save"}
                                </SecondaryButton>
                            </div>

                        </div>

                    </div>
                </div>
            </PrimaryModal>
        </>
    )
}

export default EditManageRecommendation
